import { useCallback, useState, useEffect, useRef } from 'react';
import { useDataContext } from '../context/subContexts/DataContext';
import { useAuthContext } from '../context/subContexts/AuthContext';

export const useApi = (
  apiCall,
  apiName,
  dependencies = [],
  dontCallApi,
  shouldRefresh
) => {
  const { data: storedData, updateData } = useDataContext();
  const { setTokenLogin } = useAuthContext();

  // Use useRef to persist data and pending requests across renders
  const dataRef = useRef(storedData[apiName]?.data || null);
  const pendingRequests = useRef(new Map()); // Local pending requests Map
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const prevParams = useRef(null);

  const callApi = useCallback(
    async (...args) => {
      const paramsKey = JSON.stringify(args);

      // Use cached data if available and no refresh is required
      if (
        storedData[apiName] &&
        storedData[apiName]?.params === paramsKey &&
        !shouldRefresh
      ) {
        dataRef.current = storedData[apiName].data; // Update ref synchronously
        return;
      }

      // Skip if the same API call is already pending
      if (pendingRequests.current.has(apiName) && !shouldRefresh) {
        return pendingRequests.current.get(apiName);
      }

      setLoading(true);
      setError(null);

      const apiPromise = (async () => {
        try {
          const response = await apiCall(...args);
          const newData = { data: response, params: paramsKey };

          if (response !== null) {
            updateData(apiName, newData);
            dataRef.current = response; // Update ref synchronously
          }
          prevParams.current = paramsKey;

          if (response?.token) {
            console.log('response.token', response.token);

            setTokenLogin(response.token);
          }

          return response;
        } catch (error) {
          setError(error);
          throw error;
        } finally {
          setLoading(false);
          pendingRequests.current.delete(apiName); // Clean up pending request
        }
      })();

      pendingRequests.current.set(apiName, apiPromise); // Track pending request
      return apiPromise;
    },
    [apiCall, apiName, storedData, shouldRefresh, updateData, setTokenLogin]
  );

  useEffect(() => {
    // Initialize dataRef.current if it's null
    if (dataRef.current === null && storedData[apiName]?.data) {
      dataRef.current = storedData[apiName].data;
    }

    const paramsKey = JSON.stringify(dependencies);
    if (dontCallApi) return;

    // Skip if dependencies are invalid
    if (
      !dependencies ||
      dependencies.some((dep) => dep === null || dep === undefined)
    ) {
      return;
    }

    // Call API if params change or refresh is required
    if (prevParams.current !== paramsKey || shouldRefresh) {
      prevParams.current = paramsKey;
      callApi(...dependencies);
    }
  }, [
    callApi,
    dontCallApi,
    shouldRefresh,
    ...dependencies,
    storedData,
    apiName,
  ]);

  return { data: dataRef.current, loading, error, callApi };
};
